import axios from "axios";
import cx from "classnames";
import moment from "moment";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";

import Attachments from "components/Attachments";
import { Card, CardList } from "components/Card";
import Collapsable from "components/Collapsable";
import { getPillProps, Pill } from "components/Pill";
import ResetNotesUI from "components/ResetNotesUI";
import { Body } from "components/Typography";
import TimezoneTitleSpan from "components/utils/TimezoneTitleSpan";
import Config from "config";
import CommentsUI from "containers/CommentsUI";
import { PermissionsContext } from "context";
import Constants from "helpers/constants";
import { getHeaders } from "helpers/headers";
import { getItem } from "helpers/storage";
import html2canvas from "html2canvas";
import { File } from "interfaces/file";
import { ProjectType } from "interfaces/project";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { State } from "state";
import { createAnalyticsEvent, EventTypes, FileStatus } from "state/analytics";
import { CHAIN_ROLE } from "state/auth";
import { fetchFilesByProjectStoreId } from "state/files";
import { createResetNote, fetchResetnotes } from "state/resetnotes";
import { toggleStarredStore } from "state/starredStores";
import { fetchStore, toggleStoreStarring, updateStore } from "state/store";
import { fetchStore2Projects, updateStore2Project } from "state/store2project";
import { fetchStoreCompletion, toggleStoreCompletion } from "state/store_completion";
import { fetchMySurveys } from "state/surveys";
import { PAGEVIEW_EVENTS, trackPageView } from "state/userEvents";
import { updateStoreImplementation, updateValidation } from "state/validate_store";
import { getDisplayDate, isIe } from "utils/helpers";
import { convertDateToLocalDeadlineMoment } from "utils/timezones";
import QrOverlay from "./QROverlay";
import QrPdfTemplate from "./QRPdfGenerator";

type StoreProps = RouteComponentProps<{ storeId: string; projectId: string }> & PropsFromRedux;

class Store extends React.Component<StoreProps> {
  private displaySurveys: boolean = false;

  private loadInitialCompletion = (storeId: string, projectId: string) => {
    // Uses a direct userstoremeta query — not filtered by user2stores — so admins also get their state.
    return this.props
      .fetchStoreCompletion(storeId, projectId)
      .then((data: { comments_review: number }) => {
        this.setState({ isCompleted: data?.comments_review === 1 });
      })
      .catch(() => {
        this.setState({ isCompleted: false });
      });
  };

  state = {
    loadingQr: false,
    qrStage: undefined as "generating" | "completed" | undefined,
    qrFileName: "",
    qrDataUrl: "",
    qrFileUrl: "",
    qrFileLogo: "",
    isCompleted: false,
    isTogglingCompletion: false,
  };

  public componentDidMount() {
    const {
      match: {
        params: { storeId, projectId },
      },
    } = this.props;

    // this.props.fetchLoggedUserChains();
    // this.props.fetchChains();

    return this.props.fetchStore({ projectId, storeId }).then(() => {
      const {
        store,
        user,
        location: { pathname },
      } = this.props;

      if (store && store.chain_name && store.project_type) {
        this.props.createAnalyticsEvent({
          event: EventTypes.PAGE_VIEW,
          eventType: "Pageview",
          pageName: "Store Detail",
          pageUrl: pathname,
          storeName: `${store.store_name}`,
          projectName: `${store.chain_name} - ${store.project_name}`,
          projectType: store.project_type,
        });
      }

      if (store) {
        this.props.trackPageView({
          event_type: PAGEVIEW_EVENTS.STORE_DETAIL_PAGEVIEW,
          user_id: user?.id,
          store_id: store.store_id,
          project_id: store.project_id,
          timestamp: Date.now(),
        });
      }

      this.props.fetchMySurveys(projectId).then(() => {
        if (this.props.surveys && this.props.surveys.length > 0) {
          this.displaySurveys = true;
        }
      });
      this.props.fetchFilesByProjectStoreId({ storeId, projectId }).then(() => {
        if (isIe && document.getElementsByClassName("full-width").length > 0) {
          // tslint:disable no-console
          const cardItem = document.getElementsByClassName("project-store-details-body")[0] as any;

          cardItem.style.width = "90%";
          setTimeout(() => (cardItem.style.width = "100%"), 1);
        }
      });
      this.loadInitialCompletion(storeId, projectId);

      if (this.resetNoteStateActive()) {
        this.props.fetchResetnotes({ projectId, storeId });
        this.props.fetchStore2Projects(projectId, storeId);
      }
    });
  }
  onFocus(arg0: string, onFocus: any) {
    throw new Error("Method not implemented.");
  }

  public handleToggleCompletion = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      match: {
        params: { storeId, projectId },
      },
    } = this.props;
    const previousCompleted = this.state.isCompleted;
    const newCompleted = e.target.checked;
    this.setState({ isCompleted: newCompleted, isTogglingCompletion: true });
    this.props
      .toggleStoreCompletion(storeId, projectId, newCompleted)
      .then((data: { comments_review: number }) => {
        this.setState({
          isCompleted: data?.comments_review === 1,
          isTogglingCompletion: false,
        });
      })
      .catch(() => {
        this.setState({ isCompleted: previousCompleted, isTogglingCompletion: false });
      });
  };

  public handleValidateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const source = e.currentTarget;
    const {
      match: {
        params: { storeId, projectId },
      },
      store,
      comments,
    } = this.props;

    if (store && comments) {
      if (source.name === "validation-complete") {
        this.props.createAnalyticsEvent({
          event: EventTypes.STORE_DETAIL_VALIDATION_COMPLETE,
          eventType: "Action",
          projectName: `${store.chain_name} - ${store.project_name}`,
          projectType: store.project_type as ProjectType,
          storeName: store.store_name,
          storeDetailCommentCount: comments.length,
        });
      } else if (source.name === "modify-feedback") {
        this.props.createAnalyticsEvent({
          event: EventTypes.STORE_DETAIL_MODIFY_FEEDBACK,
          eventType: "Action",
          projectName: `${store.chain_name} - ${store.project_name}`,
          projectType: store.project_type as ProjectType,
          storeName: store.store_name,
          storeDetailCommentCount: comments.length,
        });
      } else {
        // should not get here
        // TODO throw error ?
      }
    }

    this.props
      .updateValidation(storeId, projectId)
      .then(() => this.props.fetchStore({ projectId, storeId }));
  };

  public renderModifyButton() {
    return (
      <button
        name="modify-feedback"
        className="validation-button"
        type="submit"
        onClick={this.handleValidateClick}
        data-qa="modify-feedback-button"
        style={{
          // marginRight: "10px",
          padding: "5px 10px",
          outline: "none",
          border: "1px solid #E5B611",
          borderRadius: "4px",
          fontSize: "16px",
          backgroundColor: "#E5B611",
        }}
      >
        Modify Feedback
      </button>
    );
  }

  public handleCreateResetNote = (
    e: React.FormEvent,
    {
      resetNote,
      implementation,
    }: {
      resetNote: string;
      implementation: string;
    }
  ) => {
    e.preventDefault();

    const {
      match: {
        params: { storeId, projectId },
      },
      store,
    } = this.props;

    if (store) {
      const { chain_name, project_name, project_type, store_name } = store;
      this.props
        .createResetNote(
          {
            storeId,
            projectId,
            resetnote: resetNote,
            implementation,
          },
          {
            name: `${chain_name} - ${project_name}`,
            type: project_type,
            storeName: store_name,
          }
        )
        .then(() => {
          this.props.fetchResetnotes({ storeId, projectId });
        });
    }

    return true;
  };

  public goToSurveys = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const { store } = this.props;

    if (store) {
      this.props.createAnalyticsEvent({
        event: EventTypes.TAKE_SURVEY,
        eventType: "Action",
        pageName: "Store Detail",
        projectName: `${store.chain_name} - ${store.project_name}`,
        projectType: store.project_type as ProjectType,
      });
    }
    const { history } = this.props;
    const { user } = this.props;
    let route = "/surveys/me";

    if (
      user &&
      [
        Constants.AB_SYSTEM_ADMIN,
        // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
        // Constants.AB_PROJECT_ADMIN,
        Constants.INTERNAL_PROJECT_ADMIN,
        // Commented Constants.AB_REGION_CAT_DIRECTOR
        // Constants.AB_REGION_CAT_DIRECTOR,
        // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
        // Constants.COMPETITOR_CSM,
        Constants.CONTRIBUTER,
      ].some((ar) => ar === user.role)
    ) {
      route = "/surveys";
    }

    history.push(route);
  };

  public handleDateChange = (date: moment.Moment | null, type: "target" | "actual") => {
    const store2Project = this.props.store2Projects ? this.props.store2Projects[0] : undefined;
    const { store } = this.props;
    const { projectId, storeId } = this.props.match.params;
    if (store && store2Project) {
      const { project_type, project_name, store_name, chain_name } = store;

      if (type === "target") {
        this.props
          .updateStore2Project(store2Project.id, {
            // targetResetDate: moment.utc(date).format(Constants.DATE_FORMAT__YYYY_MM_DD__HH_mm_ss),
            targetResetDate: date
              ? moment.utc(date).format(Constants.DATE_FORMAT__YYYY_MM_DD__HH_mm_ss)
              : null,
          })
          .then(() => {
            this.props.fetchStore({ projectId, storeId });
          });

        this.props.createAnalyticsEvent({
          event: EventTypes.SET_TARGET_RESET_DATE,
          eventType: "Action",
          projectName: `${chain_name} - ${project_name}`,
          projectType: project_type as ProjectType,
          storeName: store_name,
          pageName: "Store Detail",
        });
      } else if (type === "actual") {
        this.props
          .updateStore2Project(store2Project.id, {
            // actualResetDate: moment.utc(date).format(Constants.DATE_FORMAT__YYYY_MM_DD__HH_mm_ss),
            actualResetDate: date
              ? moment.utc(date).format(Constants.DATE_FORMAT__YYYY_MM_DD__HH_mm_ss)
              : null,
          })
          .then(() => {
            this.props.fetchStore({ projectId, storeId });
          });

        this.props.createAnalyticsEvent({
          event: EventTypes.SET_ACTUAL_RESET_DATE,
          eventType: "Action",
          projectName: `${chain_name} - ${project_name}`,
          projectType: project_type as ProjectType,
          storeName: store_name,
          pageName: "Store Detail",
        });
      }
    }
  };

  public handleImplementationChange = (value: string) => {
    const { projectId, storeId } = this.props.match.params;

    const implementationBoolean = value === "Yes";

    this.props
      .updateStoreImplementation(Number(projectId), Number(storeId), implementationBoolean)
      .then(() => {
        this.props.fetchStore({ projectId, storeId });
      });
  };

  public resetNoteStateActive() {
    const { store } = this.props;

    const isValidationDeadlinePassed =
      store?.project_type === Constants.VALIDATION &&
      moment.utc(store.validation_deadline).isSameOrBefore(moment.utc());

    return (
      store &&
      (!store.store_status ||
        isValidationDeadlinePassed ||
        (store.store_status &&
          [
            // Constants.RESET_NOT_SCHEDULED,
            Constants.FILES_AVAILABLE,
            Constants.RESET_SCHEDULED,
            Constants.RESET_COMPLETE,
          ].indexOf(store.store_status.toUpperCase()) >= 0))
    );
  }

  // public getResetStatus(store2project: Store2Project): PillType {
  //   // if (store2project.actual_reset_date) {
  //   //   return Constants.RESET_COMPLETE;
  //   // }

  //   if (store2project.target_reset_date) {
  //     return Constants.RESET_SCHEDULED;
  //   }

  //   // return Constants.RESET_NOT_SCHEDULED;
  // }

  private waitForImages = async (container: HTMLElement) => {
    const images = Array.from(container.querySelectorAll("img"));

    await Promise.all(
      images.map((img) => {
        if (img.complete && img.naturalHeight !== 0) return Promise.resolve();

        return new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });
      })
    );
  };
  private waitForElement = (id: string): Promise<HTMLElement> => {
    return new Promise((resolve) => {
      const el = document.getElementById(id);
      if (el) {
        resolve(el);
        return;
      }
      const observer = new MutationObserver(() => {
        const element = document.getElementById(id);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  };

  private generatePdfFromHtml = async () => {
    const element = document.getElementById("qr-pdf");
    if (!element) return;
    await this.waitForImages(element);
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("portrait", "mm", "a4");
      pdf.addImage(imgData, "PNG", 0, 0, 210, 297);
      // pdf.save(`${this.state.qrFileName}-QR.pdf`);
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("PDF generation failed due to CORS/tainted canvas", error);

      this.setState({
        loadingQr: false,
        qrStage: undefined,
      });
    }
    this.setState({ qrStage: "completed" });
    setTimeout(() => {
      this.setState({
        loadingQr: false,
        qrStage: undefined,
        qrDataUrl: "",
        qrFileUrl: "",
      });
    }, 1200);
  };

  private getBase64FromUrl = async (url: string): Promise<string> => {
    // const proxyUrl = `${Config.API_SERVICE_URL}/logo-proxy?url=${encodeURIComponent(url)}`;
    const response = await fetch(url);
    if (!response.ok) {
      return "";
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  private getLogoDataUrl = async (logo?: string): Promise<string> => {
    if (!logo) {
      return "";
    }
    if (logo.startsWith("data:")) {
      return logo;
    }
    let logoUrl = logo;
    if (logoUrl.startsWith("http://")) {
      try {
        const parsed = new URL(logoUrl);
        const isLocal = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
        if (!isLocal) {
          logoUrl = logoUrl.replace("http://", "https://");
        }
      } catch {
        logoUrl = logoUrl.replace("http://", "https://");
      }
    }
    try {
      const base64Logo = await this.getBase64FromUrl(logoUrl);
      return base64Logo;
    } catch (error) {
      console.warn(`Logo CORS error (will skip logo in PDF): ${logoUrl}`);
      return "";
    }
  };

  private getPositiveId = (value: unknown): number | undefined => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  private fetchChainLogoDataUrl = async (): Promise<string> => {
    const { store } = this.props;
    let chainParentId =
      this.getPositiveId((store as any)?.chain_parent_id) ??
      this.getPositiveId((store as any)?.chain_parent);
    const chainId =
      this.getPositiveId((store as any)?.chain_id) ??
      this.getPositiveId((store as any)?.chain?.id) ??
      this.getPositiveId((store as any)?.project_chain_id);

    try {
      const response = await axios.get(`${Config.API_SERVICE_URL}/api/chains/me`, getHeaders());
      const chains = Array.isArray(response.data) ? response.data : [];
      const normalizedStoreChainName = String((store as any)?.chain_name || "")
        .trim()
        .toLowerCase();
      const matchedChain = chainId
        ? chains.find((chain: any) => this.getPositiveId(chain?.id) === chainId)
        : chains.find(
            (chain: any) =>
              String(chain?.name || "")
                .trim()
                .toLowerCase() === normalizedStoreChainName
          );
      if (!chainParentId) {
        chainParentId =
          this.getPositiveId(matchedChain?.chain_parent_id) ??
          this.getPositiveId(matchedChain?.chain_parent);
      }
    } catch {
      return "";
    }

    if (!chainParentId) return "";
    return this.getLogoDataUrl(
      `${Config.API_SERVICE_URL}/api/chain-parent-logo/${chainParentId}/image`
    );
  };

  public handleGenerateQrPdf = async (file: File) => {
    try {
      this.setState({
        loadingQr: true,
        qrStage: "generating",
        qrFileName: file.name,
        qrFileUrl: file.blob_url,
      });

      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
      const qrDataUrl = await QRCode.toDataURL(`${Config.API_SERVICE_URL}/api/file/${file.id}`, {
        width: 300,
        margin: 1,
        errorCorrectionLevel: "H",
      });

      const resolvedLogo = await this.fetchChainLogoDataUrl();
      this.setState({ qrDataUrl, qrFileLogo: resolvedLogo });
      const element = await this.waitForElement("qr-pdf");
      await sleep(1500);
      await this.generatePdfFromHtml();
    } catch (e) {
      console.error(e);
      this.setState({ loadingQr: false, qrStage: undefined });
    }
  };

  public handleAttachmentAnalytics = (action: "View" | "Download", { name, status }: File) => {
    const { store } = this.props;
    if (store) {
      this.props.createAnalyticsEvent({
        event: EventTypes.STORE_DETAIL_ATTACHMENTS,
        eventType: "Action",
        projectName: `${store.chain_name} - ${store.project_name}`,
        projectType: store.project_type as ProjectType,
        storeName: store.store_name,
        storeDetailsAttachmentAction: action,
        attachmentFileName: name,
        attachmentFileStatus: status as FileStatus,
      });
    }
  };

  public render() {
    const {
      store,
      storeError,
      starredStores,
      files,
      match: {
        params: { projectId, storeId },
      },
      isMobile,
      filesLoading,
      resetNotes = [],
      user,
    } = this.props;

    if (!store && storeError) {
      throw new Error(Constants.ERROR_404);
    }

    if (!store || !files) {
      return null;
    }

    const {
      project_name,
      project_type,
      chain_name,
      chain_logo,
      chain_logo_filename,
      validation_deadline,
      store_status,
      status,
    } = store as any;

    const store2project = this.props.store2Projects ? this.props.store2Projects[0] : undefined;
    // const isStarred =
    //   starredStores.findIndex(
    //     (s) => s.store_id === store.store_id && s.project_id === store.project_id
    //   ) > -1;

    const isFileSharing = project_type === Constants.FILE_SHARING;

    const localValidationDeadline = convertDateToLocalDeadlineMoment(
      validation_deadline,
      store?.timezone
    );
    const isAfterValidationDeadline = localValidationDeadline
      ? localValidationDeadline.isSameOrBefore(moment())
      : false;
    const isValidationDeadlinePassed =
      project_type === Constants.VALIDATION && isAfterValidationDeadline;

    let pillType = store_status || status;
 
    if (
      project_type === Constants.VALIDATION &&
      validation_deadline &&
      (!pillType || [Constants.VALIDATION_IN_PROGRESS, Constants.VALIDATION_COMPLETE].includes(pillType.toUpperCase()))
    ) {
      pillType = isValidationDeadlinePassed
        ? Constants.VALIDATION_COMPLETE
        : Constants.VALIDATION_IN_PROGRESS;
    }

    const pill = getPillProps(pillType, project_type);

    // Functionality to restrict user as per chain permission to access psa files
    let Files: File[] = [];
    if (this.props.user?.role == Constants.AB_SYSTEM_ADMIN) {
      Files = files;
    } else {
      const chainPermissionString = getItem(CHAIN_ROLE);
      const chainPermission = JSON.parse(chainPermissionString || "[]");
      const psaRestrictedRole = [
        Constants.VIEWER_NO_PSA_ACCESS,
        Constants.CONTRIBUTER_NO_PSA_ACCESS,
      ];

      if (psaRestrictedRole.includes(chainPermission.role_name)) {
        // Remove .psa files from files array
        const filteredFiles = files.filter((file) => file.extension !== "psa");
        Files = filteredFiles;
      } else {
        Files = files;
      }
    }
    const chainPermissionString = getItem(CHAIN_ROLE);
    const chainPermission = JSON.parse(chainPermissionString || "[]");
    const viewerRole = [Constants.VIEWER, Constants.VIEWER_NO_PSA_ACCESS];
    const restrictAccess = viewerRole.includes(chainPermission.role_name);
    const isSystemAdmin = this.props.user?.role === Constants.AB_SYSTEM_ADMIN;

    return (
      <PermissionsContext.Consumer>
        {({ certify_pog, view_pog, comment_pog, comment_reset }: any) => (
          <section className="project-store-details-container">
            <div
              className="project-store-details-header"
              style={{ width: "100%", display: "flex", justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <h1 className="header-text">My Work</h1>
                <Pill data-qa="project-store-details-header-pill" {...pill} />
              </div>
              {!isFileSharing &&
                !restrictAccess &&
                (store_status || status)?.toUpperCase() === "VALIDATION IN PROGRESS" && (
                  <label
                    style={{
                      display: "flex",
                      gap: "5px",
                      cursor: isSystemAdmin
                        ? "not-allowed"
                        : this.state.isTogglingCompletion
                        ? "wait"
                        : "pointer",
                      opacity: isSystemAdmin ? 0.65 : 1,
                    }}
                    title={isSystemAdmin ? "Disabled for System Admin" : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={this.state.isCompleted}
                      onChange={this.handleToggleCompletion}
                      disabled={this.state.isTogglingCompletion || isSystemAdmin}
                      data-qa="store-user-completion-toggle"
                      aria-label="Mark comments validated"
                      style={
                        {
                          transform: "scale(1.3)",
                          accentColor: "rgb(229, 182, 17)",
                          cursor: isSystemAdmin
                            ? "not-allowed"
                            : this.state.isTogglingCompletion
                            ? "wait"
                            : "pointer",
                        } as React.CSSProperties
                      }
                    />
                    {this.state.isCompleted ? "Validated" : "Mark Validated"}
                  </label>
                )}
            </div>
            <CardList
              className={cx("project-store-details-body", {
                "full-width":
                  !comment_pog ||
                  (!isFileSharing && comment_pog) ||
                  (this.resetNoteStateActive() && !comment_reset),
              })}
            >
              <div>
                <div
                  style={{ borderRadius: "16px", border: "1px solid #e3e6ea", overflow: "hidden" }}
                >
                  <Collapsable
                    header={
                      <div
                        style={{
                          padding: "10px 20px 10px 20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <h3
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: "16px",
                          }}
                        >
                          Store Details
                          {/* <StarToggle
                            clickHandler={async (starred: any) => {
                              await this.props.toggleStarredStore({
                                user_id: user?.id,
                                store_id: store.store_id,
                                project_id: store.project_id,
                                store_name: store.store_name,
                                chain_name: store.chain_name,
                                project_name: store.project_name,
                                project_type: store.project_type,
                                starred,
                              });
                            }}
                            isActive={isStarred}
                          /> */}
                        </h3>
                      </div>
                    }
                  >
                    <Card.Body className="store-details-container">
                      <label>
                        <h6>Chain</h6>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          {/* <Base64Logo data={chain_logo} logo_filename={chain_logo_filename} /> */}
                          <Body type="1" style={{ marginLeft: 0 }}>
                            {chain_name}
                          </Body>
                        </div>
                      </label>
                      <label>
                        <h6>Project</h6>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          {/* <Base64Logo data={chain_logo} logo_filename={chain_logo_filename} /> */}
                          <Body type="1" style={{ marginLeft: 0 }}>
                            {project_name}
                          </Body>
                        </div>
                      </label>
                      {store.retail_store_num && (
                        <label>
                          <h6>Store No</h6>
                          <Body type="1" data-qa="store-number">
                            {store.retail_store_num}
                          </Body>
                        </label>
                      )}
                      {store.store_name && (
                        <label>
                          <h6>Store Name</h6>
                          <Body type="1" data-qa="store-name">
                            {store.store_name}
                          </Body>
                        </label>
                      )}
                      {store.store_address && (
                        <label>
                          <h6>Address</h6>
                          <Body type="1" data-qa="address">
                            {store.store_address}
                          </Body>
                        </label>
                      )}
                      {store.validation_deadline && (
                        <label>
                          <h6>Validation Deadline</h6>
                          <Body type="1" data-qa="validation-deadline">
                            <TimezoneTitleSpan
                              dateValue={store.validation_deadline}
                              timezone={store?.timezone}
                            >
                              {getDisplayDate({
                                date: store.validation_deadline,
                                timezone: store?.timezone,
                              })}
                            </TimezoneTitleSpan>
                          </Body>
                        </label>
                      )}
                      {store.abinbev_wholesaler && (
                        <label>
                          <h6>AB-INBEV WHOLESALER</h6>
                          <Body type="1" data-qa="ab-inbev-wholesaler">
                            {store.abinbev_wholesaler}
                          </Body>
                        </label>
                      )}
                      {store.coors_wholesaler && (
                        <label>
                          <h6>COORS WHOLESALER</h6>
                          <Body type="1" data-qa="coors-wholesaler">
                            {store.coors_wholesaler}
                          </Body>
                        </label>
                      )}
                      {store.miller_wholesaler && (
                        <label>
                          <h6>MILLER WHOLESALER</h6>
                          <Body type="1" data-qa="miller-wholesaler">
                            {store.miller_wholesaler}
                          </Body>
                        </label>
                      )}
                      {store.third_party_wholesaler && (
                        <label>
                          <h6>3RD PARTY WHOLESALER</h6>
                          <Body type="1" data-qa="third-party-wholesaler">
                            {store.third_party_wholesaler}
                          </Body>
                        </label>
                      )}
                      {store.ad_zone && (
                        <label>
                          <h6>AD ZONE</h6>
                          <Body type="1" data-qa="adzone">
                            {store.ad_zone}
                          </Body>
                        </label>
                      )}
                      {store.cluster_store_group && (
                        <label>
                          <h6>CLUSTER/STORE GROUP</h6>
                          <Body type="1" data-qa="group">
                            {store.cluster_store_group}
                          </Body>
                        </label>
                      )}
                    </Card.Body>
                  </Collapsable>
                </div>
                {view_pog && (
                  <Attachments
                    attachments={Files}
                    loading={filesLoading}
                    onDownloadOrView={this.handleAttachmentAnalytics}
                    onGenerateQr={this.handleGenerateQrPdf}
                  />
                )}
              </div>
              {(// comment_reset
              (!restrictAccess &&
                this.resetNoteStateActive() &&
                (isFileSharing || isValidationDeadlinePassed)) ||
                (!isFileSharing && !isValidationDeadlinePassed && !restrictAccess) ||
                certify_pog) && (
                <div>
                  {!restrictAccess &&
                  this.resetNoteStateActive() &&
                  (isFileSharing || isValidationDeadlinePassed) ? (
                    <ResetNotesUI
                      onSubmit={this.handleCreateResetNote}
                      onTakeSurveys={this.goToSurveys}
                      resetnotes={resetNotes}
                      displaySurveys={this.displaySurveys}
                      store2project={store2project || []}
                      onDateChange={this.handleDateChange}
                      storeStatus={store.store_status || store.status}
                      onImplementationChange={this.handleImplementationChange}
                      isMobile={isMobile}
                    />
                  ) : (
                    !isFileSharing &&
                    !isValidationDeadlinePassed &&
                    !restrictAccess && <CommentsUI storeId={storeId} projectId={projectId} />
                  )}
                </div>
              )}
            </CardList>

            {this.state.loadingQr && (
              <QrOverlay
                qrStage={this.state.qrStage}
                fileName={this.state.qrFileName}
                generatingText="Generating the QR code for the file..."
                successText="Successfully downloaded the Project file."
              />
            )}

            {this.state.qrDataUrl && (
              <QrPdfTemplate
                store={store}
                qrDataUrl={this.state.qrDataUrl}
                qrFileUrl={this.state.qrFileUrl}
                fileLogo={this.state.qrFileLogo}
              />
            )}
          </section>
        )}
      </PermissionsContext.Consumer>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    auth_user: state.auth_user.content,
    comments: state.comments.content,
    files: state.files.content?.data,
    filesLoading: state.files.loading,
    store: state.store.content,
    storeError: state.store.error,
    starredStores: state.starredStores.content,
    products: state.products.content,
    resetNotes: state.resetnotes.content,
    surveys: state.surveys.content,
    store2Projects: state.store2Projects.content,
    user: state.auth_user.content,
    isMobile: state.breakpoint.content === Constants.BREAKPOINT_MOBILE,
  }),
  {
    createAnalyticsEvent,
    fetchStore,
    updateStore,
    toggleStoreStarring,
    updateValidation,
    updateStoreImplementation,
    fetchFilesByProjectStoreId,
    fetchResetnotes,
    createResetNote,
    fetchMySurveys,
    fetchStore2Projects,
    updateStore2Project,
    toggleStarredStore,
    trackPageView,
    fetchStoreCompletion,
    toggleStoreCompletion,
  }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default withRouter(connecter(Store));
